import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Edit, ChevronUp, ChevronDown } from 'lucide-react';
import type { Strategy, StrategyFilters } from '@/types/strategy';

interface StrategiesTableProps {
  strategies: Strategy[];
  isLoading: boolean;
  isEmpty?: boolean;
  sortBy: StrategyFilters['sortBy'];
  sortOrder: StrategyFilters['sortOrder'];
  onSort: (column: StrategyFilters['sortBy']) => void;
}

function SortableHeader({
  column,
  label,
  sortBy,
  sortOrder,
  onSort,
}: {
  column: StrategyFilters['sortBy'];
  label: string;
  sortBy: StrategyFilters['sortBy'];
  sortOrder: StrategyFilters['sortOrder'];
  onSort: (column: StrategyFilters['sortBy']) => void;
}) {
  const isActive = sortBy === column;
  return (
    <th className="px-4 py-3 text-left">
      <button
        className="flex items-center gap-1 text-sm font-medium text-pine-ink-soft hover:text-pine-ink"
        onClick={() => onSort(column)}
      >
        <span>{label}</span>
        <div className="flex flex-col -space-y-1">
          <ChevronUp className={`w-3 h-3 ${isActive && sortOrder === 'asc' ? 'text-pine-pine' : ''}`} />
          <ChevronDown className={`w-3 h-3 ${isActive && sortOrder === 'desc' ? 'text-pine-pine' : ''}`} />
        </div>
      </button>
    </th>
  );
}

function CategoryBadge({ name, label }: { name: string; label: string }) {
  const colorMap: Record<string, string> = {
    sunny: 'bg-pine-l1 text-pine-l5 border-pine-l2',
    hazy: 'bg-pine-l1 text-pine-pine border-pine-l2',
    rainy: 'bg-pine-l1 text-pine-pine border-pine-l2',
    stormy: 'bg-pine-l1 text-pine-pine border-pine-l2',
  };
  const colors = colorMap[label] || 'bg-pine-l1 text-pine-pine border-pine-l2';

  return (
    <span className={`inline-flex items-center rounded-pine-pill px-2.5 py-0.5 text-pine-caption font-semibold border-[1.5px] ${colors}`}>
      {name}
    </span>
  );
}

function StrategyStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    published: {
      label: 'Published',
      className: 'text-pine-approved border-pine-approved bg-pine-approved/10',
    },
    archived: {
      label: 'Archived',
      className: 'text-pine-draft border-pine-draft bg-pine-draft/10',
    },
  };
  const c = config[status] || config.published;

  return (
    <span className={`inline-flex items-center rounded-pine-pill px-2.5 py-0.5 text-pine-caption font-semibold border-[1.5px] ${c.className}`}>
      {c.label}
    </span>
  );
}

export default function StrategiesTable({
  strategies,
  isLoading,
  isEmpty = false,
  sortBy,
  sortOrder,
  onSort,
}: StrategiesTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="admin-card overflow-hidden">
        <div className="animate-pulse space-y-4 p-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-pine-line rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No strategies yet</h3>
        <p className="text-pine-ink-soft mt-2">Get started by creating your first strategy</p>
        <button
          onClick={() => navigate('/admin/strategies/new')}
          className="admin-button-primary mt-4"
        >
          Create Your First Strategy
        </button>
      </div>
    );
  }

  if (strategies.length === 0) {
    return (
      <div className="admin-card p-12 text-center">
        <h3 className="text-lg font-semibold text-pine-ink">No strategies found</h3>
        <p className="text-pine-ink-soft mt-2">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="admin-card overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-pine-line">
            <SortableHeader column="title" label="Title" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <SortableHeader column="category" label="Category" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <SortableHeader column="sortOrder" label="Order" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
            <th className="px-4 py-3 text-left text-sm font-medium text-pine-ink-soft">Status</th>
            <th className="px-4 py-3 text-right text-sm font-medium text-pine-ink-soft">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-pine-line">
          {strategies.map((strategy, index) => (
            <motion.tr
              key={strategy.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="hover:bg-pine-hover-item transition-colors"
            >
              <td className="px-4 py-3">
                <button
                  className="text-pine-ink hover:text-pine-pine font-medium text-left"
                  onClick={() => navigate(`/admin/strategies/${strategy.id}`)}
                >
                  {strategy.title}
                </button>
                <div className="text-sm text-pine-ink-soft line-clamp-1 mt-0.5">
                  {strategy.subtitle}
                </div>
              </td>
              <td className="px-4 py-3">
                <CategoryBadge name={strategy.category.name} label={strategy.category.label} />
              </td>
              <td className="px-4 py-3 text-pine-ink">
                {strategy.sortOrder}
              </td>
              <td className="px-4 py-3">
                <StrategyStatusBadge status={strategy.status} />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/strategies/${strategy.id}`)}
                    className="p-1.5 rounded-lg hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="View"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/strategies/${strategy.id}/edit`)}
                    className="p-1.5 rounded-lg hover:bg-pine-hover-item text-pine-ink-soft hover:text-pine-ink transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
